import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/** A number with a label. Six of these make a dashboard row. */
export function Stat({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'default' | 'good' | 'warn';
}) {
  const toneClass =
    tone === 'good' ? 'text-emerald-700' : tone === 'warn' ? 'text-savanna-600' : 'text-ink-900';

  return (
    <div className="card">
      <p className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold tabular-nums ${toneClass}`}>{value}</p>
      {hint && <p className="mt-0.5 text-xs text-ink-500">{hint}</p>}
    </div>
  );
}

export function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: { label: string; to: string };
  children: ReactNode;
}) {
  return (
    <section className="mt-6">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">{title}</h2>
        {action && (
          <Link to={action.to} className="text-xs text-brand-700 underline">
            {action.label}
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="card text-sm text-ink-600">{children}</div>;
}

/**
 * A horizontal bar, in a table cell.
 *
 * Deliberately not a charting library. Twelve rows of "how far through is this
 * school" is a bar per row, and pulling in a charting dependency for it would
 * cost more bytes than every dashboard in this app put together — on the
 * connections this is built for, that is the whole decision.
 */
export function Meter({ value, max = 100 }: { value: number; max?: number }) {
  const percent = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 overflow-hidden rounded-full bg-ink-200">
        <div className="h-full bg-brand-600" style={{ width: `${percent}%` }} />
      </div>
      <span className="text-xs tabular-nums text-ink-600">{percent}%</span>
    </div>
  );
}
